// Authorization utilities for user permissions
// This module handles authorization logic for specific features like responsible fields

import authConfig from "../authConfig";
import userAccessService from "../services/userAccessService";

/**
 * Signs the user out by clearing local storage and redirecting to the
 * Cognito hosted logouts endpoint, which invalidates the SSO session cookie
 * and bounces back to the app's root (which then redirects to /signin).
 */
export const signOut = () => {
	localStorage.clear();
	const logoutUri = encodeURIComponent(authConfig.redirectUri);
	window.location.href =
		`https://${authConfig.cognitoDomain}/logout` +
		`?client_id=${authConfig.clientId}` +
		`&logout_uri=${logoutUri}`;
};

/**
 * List of authorized email addresses for responsible and followup date fields
 * These users have special WRITE access to protected fields regardless of Cognito access level
 */
export const AUTHORIZED_EMAILS = [
	// Original authorized users
	"yash.sakhare@man.eu",
	"uday.gudla-ext@man.eu",
	"anuradha.shukla@man.eu",
	"vishal.talwar@man.eu",
	"dhiraj.chaudhari@man.eu",
	"abhilash.walunj@man.eu",
	"anagha.shinde@man.eu",
	"tanmay.bhosale@man.eu",
	"pankaj.thete@man.eu",
];

/**
 * Checks if the current user is authorized to edit responsible fields and followup dates
 * @param {Function} getUserEmail - Function that returns the user's email address
 * @returns {boolean} - True if user is authorized, false otherwise
 *
 * UPDATED: Now checks database write access in addition to hardcoded list
 * Users with 'write' access in Cognito database can edit protected fields
 */
export const isUserAuthorizedForResponsibleFields = (getUserEmail) => {
	if (!getUserEmail) {
		return false;
	}

	const userEmail = getUserEmail();

	if (!userEmail || typeof userEmail !== "string") {
		return false;
	}

	// Check hardcoded authorized list (legacy support)
	const isInHardcodedList = AUTHORIZED_EMAILS.includes(userEmail.toLowerCase());

	// Check database write access (new method)
	const cachedAccess = userAccessService.getCachedUserAccess(userEmail);
	const hasWriteAccess = cachedAccess?.canWrite || false;

	// User is authorized if they're in hardcoded list OR have database write access
	return isInHardcodedList || hasWriteAccess;
};

/**
 * Checks if a specific email address is authorized
 * @param {string} email - Email address to check
 * @returns {boolean} - True if email is authorized, false otherwise
 *
 * UPDATED: Now checks database write access in addition to hardcoded list
 */
export const isEmailAuthorized = (email) => {
	if (!email || typeof email !== "string") {
		return false;
	}

	// Check hardcoded authorized list (legacy support)
	const isInHardcodedList = AUTHORIZED_EMAILS.includes(email.toLowerCase());

	// Check database write access (new method)
	const cachedAccess = userAccessService.getCachedUserAccess(email);
	const hasWriteAccess = cachedAccess?.canWrite || false;

	// User is authorized if they're in hardcoded list OR have database write access
	return isInHardcodedList || hasWriteAccess;
};

/**
 * Gets a list of all authorized emails (for admin purposes)
 * @returns {string[]} - Array of authorized email addresses
 */
export const getAuthorizedEmails = () => {
	return [...AUTHORIZED_EMAILS]; // Return a copy to prevent modification
};

/**
 * Check if user has write access based on Cognito database
 * Falls back to AUTHORIZED_EMAILS if service fails
 * @param {Function} getUserEmail - Function that returns the user's email address
 * @returns {Promise<boolean>} - True if user has write access
 */
export const canUserWrite = async (getUserEmail) => {
	if (!getUserEmail) {
		return false;
	}

	const userEmail = getUserEmail();

	if (!userEmail || typeof userEmail !== "string") {
		return false;
	}

	try {
		// Check Cognito database for user access level
		const canWrite = await userAccessService.canUserWrite(userEmail);

		// Fallback to AUTHORIZED_EMAILS for special protected fields
		const isAuthorizedForProtectedFields = isEmailAuthorized(userEmail);

		console.log(
			`🔐 User ${userEmail} write access: ${canWrite}, protected fields: ${isAuthorizedForProtectedFields}`,
		);

		return canWrite || isAuthorizedForProtectedFields;
	} catch (error) {
		console.error("Error checking write access:", error);
		// Fallback to AUTHORIZED_EMAILS on error
		return isEmailAuthorized(userEmail);
	}
};

/**
 * Check if user has write access (synchronous version using email string)
 * @param {string} email - User's email address
 * @returns {Promise<boolean>} - True if user has write access
 */
export const canEmailWrite = async (email) => {
	if (!email || typeof email !== "string") {
		return false;
	}

	try {
		const canWrite = await userAccessService.canUserWrite(email);
		const isAuthorizedForProtectedFields = isEmailAuthorized(email);

		return canWrite || isAuthorizedForProtectedFields;
	} catch (error) {
		console.error("Error checking write access:", error);
		return isEmailAuthorized(email);
	}
};

/**
 * Get full user access information
 * @param {string} email - User's email address
 * @returns {Promise<Object>} - User access data
 */
export const getUserAccessInfo = async (email) => {
	if (!email || typeof email !== "string") {
		return {
			can_write: false,
			user_access: "read",
			user_role: "user",
			is_admin: false,
		};
	}

	try {
		return await userAccessService.getUserAccess(email);
	} catch (error) {
		console.error("Error getting user access info:", error);
		return {
			can_write: isEmailAuthorized(email),
			user_access: "read",
			user_role: "user",
			is_admin: false,
		};
	}
};

/**
 * Clear user access cache (useful when permissions change)
 * @param {string} email - Optional email to clear specific user cache
 */
export const clearUserAccessCache = (email = null) => {
	userAccessService.clearCache(email);
};

/**
 * Preload user access on app startup
 * @param {string} email - User's email address
 */
export const preloadUserAccess = async (email) => {
	if (email) {
		await userAccessService.preloadUserAccess(email);
	}
};
